import pMap from "p-map";

/** Latency budget for first-object walks on bulk viewer selections. Remainder stays raw. */
export const BULK_SELECTION_RESOLVE_CAP = 200;

const RESOLVE_CONCURRENCY = 5;

/**
 * Apply a box's toggle delta to the semantic selection.
 *
 * Additions win over removals: a box that covers one selected fragment and one unselected
 * fragment of the same object leaves that object painted, so it stays selected.
 */
export function applySelectionDelta(
    previousMainObjects: readonly number[],
    added: readonly number[],
    removed: readonly number[],
): number[] {
    const removedSet = new Set(removed);
    const seen = new Set<number>();
    const mainObjects: number[] = [];

    for (const id of previousMainObjects) {
        if (!removedSet.has(id) && !seen.has(id)) {
            seen.add(id);
            mainObjects.push(id);
        }
    }

    for (const id of added) {
        if (!seen.has(id)) {
            seen.add(id);
            mainObjects.push(id);
        }
    }

    return mainObjects;
}

/**
 * Resolve a box's toggle delta the way a single click would, then fold it into the
 * previous semantic selection.
 *
 * The delta — not the resulting highlight set — is the input, because `highlighted` is a
 * superset of the semantic selection: Select Similar paints matches plus descendants, and a
 * First Object click paints the resolved subtree. Re-resolving the paint set would replace
 * `mainObjects` with descendant geometry and spend the whole budget on the lowest ids of an
 * accumulated selection, leaving every newly boxed hit unresolved.
 *
 * - `removed` and `added` share one {@link BULK_SELECTION_RESOLVE_CAP} budget, removals
 *   first: an unresolved removal leaves a row selected that the user just boxed out, where
 *   an unresolved addition only costs a raw id.
 * - A resolve that returns `undefined` keeps the raw id (same floor as `resolveSelectionTarget`).
 * - `signal` aborts `p-map` so a superseded box does not finish the walk.
 *
 * @see https://procoretech.atlassian.net/wiki/x/AYAQZgE — "Box select is a multi-select
 *      from the canvas. The same rule as a single click, applied per hit."
 */
export async function resolveSelectionDelta(
    previousMainObjects: readonly number[],
    delta: { added: readonly number[]; removed: readonly number[] },
    resolveOne: (id: number) => Promise<number | undefined>,
    options?: { signal?: AbortSignal },
): Promise<number[]> {
    const removedBudget = Math.min(delta.removed.length, BULK_SELECTION_RESOLVE_CAP);
    const removed = await resolveHead(delta.removed, removedBudget, resolveOne, options?.signal);
    const added = await resolveHead(
        delta.added,
        BULK_SELECTION_RESOLVE_CAP - removedBudget,
        resolveOne,
        options?.signal,
    );

    return applySelectionDelta(previousMainObjects, added, removed);
}

/** Resolve the first `cap` ids; the rest pass through raw. */
async function resolveHead(
    ids: readonly number[],
    cap: number,
    resolveOne: (id: number) => Promise<number | undefined>,
    signal?: AbortSignal,
): Promise<number[]> {
    const resolved = await pMap(ids.slice(0, cap), async (id): Promise<number> => (await resolveOne(id)) ?? id, {
        concurrency: RESOLVE_CONCURRENCY,
        signal,
        stopOnError: true,
    });

    return [...resolved, ...ids.slice(cap)];
}

/**
 * Keep the current primary when it is still in the resolved semantic set.
 * Box select's highlight set is raw geometry, so "still painted" is the wrong keep rule.
 */
export function pickKeptPrimary(
    resolvedMainObjects: readonly number[],
    previousMainObject: number | undefined,
): number | undefined {
    if (previousMainObject !== undefined && resolvedMainObjects.includes(previousMainObject)) {
        return previousMainObject;
    }
    return resolvedMainObjects[0];
}
